import React, { useEffect, useState } from 'react';
import { Capacitor } from '@capacitor/core';
import { AppUpdate, AppUpdateAvailability } from '@capawesome/capacitor-app-update';
import { useTheme } from '../../contexts/ThemeContext';
import { btn } from '../../utils/buttonStyles';

// Apple's numeric App Store ID for kynaixsports — openAppStore() needs this
// explicitly on iOS (unlike Android, where it defaults to the current app's
// package name if omitted). See ios/App/App/public/get-app.html for the
// matching apps.apple.com link this was taken from.
const IOS_APP_STORE_ID = '6761959059';

// Checks the live App Store / Play Store listing for a newer version than
// what's installed, once per app launch, and offers to send the user there.
// Native-only: there's no store listing to compare against on the web build.
// Every native call is wrapped defensively — any failure (plugin not
// available, no network, app not installed via a store, lookup not yet
// indexed, etc.) just means "nothing to show", logged and swallowed, never
// surfaced to the user or allowed to crash the app.
export default function UpdateAvailableModal() {
  const { theme } = useTheme();
  const [updateAvailable, setUpdateAvailable] = useState(false);
  const [opening, setOpening] = useState(false);

  useEffect(() => {
    if (!Capacitor.isNativePlatform()) return;

    let cancelled = false;
    (async () => {
      try {
        const info = await AppUpdate.getAppUpdateInfo();
        if (!cancelled && info.updateAvailability === AppUpdateAvailability.UPDATE_AVAILABLE) {
          setUpdateAvailable(true);
        }
      } catch (error) {
        console.error('[UpdateAvailableModal] Failed to check for app update:', error?.message ?? error);
      }
    })();

    return () => { cancelled = true; };
  }, []);

  if (!updateAvailable) return null;

  const handleUpdate = async () => {
    setOpening(true);
    try {
      await AppUpdate.openAppStore({ appId: IOS_APP_STORE_ID });
    } catch (error) {
      console.error('[UpdateAvailableModal] Failed to open app store:', error?.message ?? error);
    } finally {
      setOpening(false);
      setUpdateAvailable(false);
    }
  };

  const handleCancel = () => setUpdateAvailable(false);

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
      <div className={`${theme.card} rounded-xl shadow-2xl p-6 max-w-sm w-full mx-4`}>
        <div className="flex items-center gap-3 mb-4">
          <div className="w-10 h-10 rounded-full bg-blue-100 flex items-center justify-center flex-shrink-0">
            <span className="text-blue-600 text-xl">⬆️</span>
          </div>
          <div>
            <h3 className={`font-semibold text-lg ${theme.text}`}>Update Available</h3>
            <p className={`text-sm ${theme.textMuted}`}>A newer version of Kynaix Sports is ready</p>
          </div>
        </div>

        <div className="flex gap-3">
          <button
            onClick={handleUpdate}
            disabled={opening}
            className={`flex-1 ${btn.base} ${btn.md} ${theme.buttonPrimary} disabled:opacity-40 disabled:cursor-not-allowed`}
          >
            {opening ? 'Opening...' : 'Update'}
          </button>
          <button
            onClick={handleCancel}
            className={`${btn.base} ${btn.md} ${theme.buttonGhost}`}
          >
            Cancel
          </button>
        </div>
      </div>
    </div>
  );
}
